import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Background sleep mode: once the page has been hidden (app minimised) for 2 min and no call is active,
// periodic timers are skipped so the phone stays cool. Calls/messages still arrive via push. Resumes on the next visible.
(function installBackgroundSleep() {
  if (typeof window === 'undefined') return;
  const nativeSetInterval = window.setInterval.bind(window);
  let hiddenAt = document.hidden ? Date.now() : 0;
  document.addEventListener('visibilitychange', () => { hiddenAt = document.hidden ? Date.now() : 0; });
  window.__bbcAsleep = () => hiddenAt > 0 && Date.now() - hiddenAt > 120000 && !window.__bbcCallActive;
  window.setInterval = function (fn, ms, ...args) {
    if (typeof fn !== 'function' || fn.__bbcEssential) return nativeSetInterval(fn, ms, ...args);
    return nativeSetInterval(function () { if (!window.__bbcAsleep()) fn.apply(this, args); }, ms);
  };
})();

// Register Service Worker for PWA with bullet-proof auto-update

// Show a friendly, non-blocking "Updating to the latest version…" toast for
// ~3 seconds, then run the reload/force-update. Vanilla DOM so it works even
// before React/sonner mounts and outside the React tree (index.js scope).
const showUpdatingOverlayThenReload = (doReload, message) => {
  try {
    if (!document.getElementById('bbc-updating-toast')) {
      if (!document.getElementById('bbc-updating-style')) {
        const style = document.createElement('style');
        style.id = 'bbc-updating-style';
        style.textContent = '@keyframes bbcspin{to{transform:rotate(360deg)}}@keyframes bbcin{from{opacity:0;transform:translate(-50%,-8px)}to{opacity:1;transform:translate(-50%,0)}}';
        document.head.appendChild(style);
      }
      const el = document.createElement('div');
      el.id = 'bbc-updating-toast';
      el.setAttribute('role', 'status');
      el.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:2147483647;background:#0f766e;color:#fff;padding:10px 18px;border-radius:9999px;font:500 14px/1.2 system-ui,-apple-system,sans-serif;box-shadow:0 8px 30px rgba(0,0,0,.28);display:flex;align-items:center;gap:9px;animation:bbcin .25s ease-out';
      el.innerHTML = '<span style="width:14px;height:14px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;display:inline-block;animation:bbcspin .7s linear infinite"></span><span>' + (message || 'Updating to the latest version…') + '</span>';
      document.body.appendChild(el);
    }
  } catch (_) { /* DOM not ready — reload anyway */ }
  setTimeout(() => { try { doReload(); } catch (_) { window.location.reload(); } }, 3000);
};
if (typeof window !== 'undefined') window.__bbcShowUpdating = showUpdatingOverlayThenReload;

// Chrome's own "Install app" (PWA / WebAPK "v1.0") prompt confused users into installing the browser wrapper instead of
// the real BBC APK — swallow it; the /download page offers the latest APK instead.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); window.__bbcPwaPrompt = e; });
}


const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return;

  let refreshing = false;
  const forceReload = (reason) => {
    if (refreshing) return;
    refreshing = true;
    try { console.log('[PWA] Reloading for new version:', reason); } catch (_) {}
    // Show a friendly 3s "Updating…" toast so the reload isn't a jarring surprise.
    showUpdatingOverlayThenReload(() => window.location.reload());
  };

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });

    // Apply any SW that's already waiting from a previous visit
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    // Check for updates immediately and every 60 seconds
    registration.update();
    setInterval(() => registration.update(), 60 * 1000);

    // Also check for updates when the tab becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        try { registration.update(); } catch (_) {}
      }
    });

    // Handle new SW found
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New SW ready — skip waiting immediately
          newWorker.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });

    // Reload when the new SW takes control (Chrome/Edge/Firefox happy path)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      forceReload('controllerchange');
    });

    // Reload when SW broadcasts SW_UPDATED (covers TWA / WebView / iOS Safari
    // which sometimes don't fire controllerchange after clients.claim()).
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        console.log('[PWA] SW updated to', event.data.version);
        forceReload('SW_UPDATED message');
      }
      if (event.data?.type === 'OFFLINE_QUEUED') {
        console.log('[PWA] Request queued for sync:', event.data.url);
      }
      if (event.data?.type === 'SYNC_COMPLETE') {
        console.log('[PWA] Background sync complete. Synced:', event.data.synced);
        if (event.data.synced > 0) {
          window.dispatchEvent(new CustomEvent('background-sync-complete', { detail: event.data }));
        }
      }
    });

    // Pre-cache API data when user is authenticated
    const token = localStorage.getItem('token');
    if (token && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'PRECACHE_API', token });
    }
  } catch (error) {
    console.error('[PWA] Service Worker registration failed:', error);
  }
};

// Expose a global force-update function for the user-menu "Check for
// Updates" item.  Available even if the SW isn't registered (e.g.
// during an outage of the SW infrastructure).
window.__bbcForceUpdate = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        try {
          if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          if (reg.active) reg.active.postMessage({ type: 'CLEAR_ALL_CACHES' });
          await reg.unregister();
        } catch (_) { /* keep going */ }
      }
    }
    if ('caches' in window) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      } catch (_) { /* ignore */ }
    }
  } finally {
    // Hard reload with a unique cache-busting query string so Chrome's
    // HTTP cache cannot serve a stale index.html.  Replaces the URL so
    // history isn't polluted with `?_refresh=…` entries.
    const sep = window.location.search ? '&' : '?';
    const url = `${window.location.pathname}${window.location.search}${sep}_refresh=${Date.now()}${window.location.hash}`;
    window.location.replace(url);
  }
};

// Register SW after page load
window.addEventListener('load', registerServiceWorker);

// ---------------------------------------------------------------------------
// Direct build-version auto-updater (belt-and-braces for the SW update path).
// While the app stays open, poll /build-version.json (bypassing SW + HTTP
// cache). If the server has a newer build than the one baked into this bundle,
// auto force-update — no manual tap required. This deterministically closes
// the "app left open for hours on a TWA whose SW update() silently failed" gap.
// ---------------------------------------------------------------------------
const startVersionAutoUpdate = () => {
  const baked = (typeof window !== 'undefined' && window.__BBC_BUILD_VERSION__) || '';
  // Skip when the version wasn't injected (local dev) to avoid reload loops.
  if (!baked || baked === '0' || baked.indexOf('__') === 0) return;

  let busy = false;
  const check = async () => {
    if (busy || document.visibilityState === 'hidden') return;
    busy = true;
    try {
      const r = await fetch(`/build-version.json?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!r.ok) return;
      const j = await r.json();
      if (j && j.version && String(j.version) !== String(baked)) {
        // Only auto-refresh once per target version per session (loop guard).
        const guardKey = 'bbc_autoupdate_target';
        if (sessionStorage.getItem(guardKey) === String(j.version)) return;
        sessionStorage.setItem(guardKey, String(j.version));
        try { console.log('[PWA] New build detected — auto-updating', baked, '->', j.version); } catch (_) {}
        const doUpdate = () => {
          if (typeof window.__bbcForceUpdate === 'function') window.__bbcForceUpdate();
          else window.location.reload();
        };
        showUpdatingOverlayThenReload(doUpdate);
      }
    } catch (_) {
      /* offline / transient — try again next tick */
    } finally {
      busy = false;
    }
  };

  // Check shortly after load, then every 2 minutes, and whenever the tab
  // regains focus (covers a TWA returning from the background).
  setTimeout(check, 15 * 1000);
  setInterval(check, 2 * 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
};
window.addEventListener('load', startVersionAutoUpdate);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
